import type { CreateGeneralUserRequestBody, UpdateGeneralUserRequestBody, User } from '@engagement-web-app/common';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, type ErrorComponentProps, useRouter } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/Button';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Pill } from '@/components/Pill';
import { Section, SectionHeader } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { showErrorToast, showSuccessToast } from '@/components/Toasts';
import { type AddUserFormData, UserFormDialog } from '@/components/UserFormDialog';
import { fetchOrganization } from '@/queries/organizations';
import {
    createGeneralUser,
    deleteGeneralUser,
    fetchSystemGeneralUsers,
    resetGeneralUserMfa,
    resetGeneralUserPassword,
    updateGeneralUser,
} from '@/queries/systemGeneralUsers';
import { fetchUserRoles } from '@/queries/user-roles/system';
import { UsersSection, UsersSectionSkeleton } from '../-fragments/UsersSection';

export const Route = createFileRoute('/system/_auth/organizations/$organizationId/users/')({
    loader: async ({ params }) => {
        const { organizationId } = params;
        const [org, users, userRoles] = await Promise.all([
            fetchOrganization({ uuid: organizationId }),
            // TODO: Need to implement pagination?
            fetchSystemGeneralUsers(organizationId, { from: 0 }),
            fetchUserRoles(),
        ]);
        return { org, users, userRoles };
    },
    pendingComponent: SystemShowOrganizationUsersRouteSkeleton,
    errorComponent: SystemShowOrganizationUsersRouteError,
    component: SystemShowOrganizationUsersRoute,
});

function SystemShowOrganizationUsersRouteError({ reset }: ErrorComponentProps) {
    return <ErrorOverlay active onRetry={reset} />;
}

function SystemShowOrganizationUsersRoute() {
    const { org, users, userRoles } = Route.useLoaderData();
    const router = useRouter();

    const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);

    const createUserMutation = useMutation({
        mutationFn: (body: CreateGeneralUserRequestBody) => createGeneralUser(org.uuid, body),
        onSuccess() {
            router.invalidate();
            showSuccessToast({ title: 'ユーザー追加', description: 'ユーザーが追加されました。' });
        },
        onError(error) {
            console.error('Failed to create user:', error);
            showErrorToast({ title: 'ユーザー追加', description: 'ユーザーの追加に失敗しました。' });
        },
    });

    const editUserMutation = useMutation({
        mutationFn: ({ user, body }: { user: User; body: UpdateGeneralUserRequestBody }) => {
            return updateGeneralUser(user.organizationId, user.uuid, body);
        },
        onSuccess() {
            router.invalidate();
            showSuccessToast({ title: 'ユーザー編集', description: 'ユーザー情報が更新されました。' });
        },
        onError(error) {
            console.error('Failed to create user:', error);
            showErrorToast({ title: 'ユーザー編集', description: 'ユーザー情報の更新に失敗しました。' });
        },
    });

    const deleteUserMutation = useMutation({
        mutationFn: ({ organizationId, uuid }: { organizationId: string; uuid: string }) => {
            return deleteGeneralUser(organizationId, uuid);
        },
        onSuccess() {
            router.invalidate();
            showSuccessToast({ title: 'ユーザー削除', description: 'ユーザーが削除されました。' });
        },
        onError(error) {
            console.error('Failed to delete user:', error);
            showErrorToast({ title: 'ユーザー削除', description: 'ユーザーの削除に失敗しました。' });
        },
    });

    const resetUserPasswordMutation = useMutation({
        mutationFn: ({ organizationId, uuid }: { organizationId: string; uuid: string }) => {
            return resetGeneralUserPassword(organizationId, uuid);
        },
        onSuccess() {
            showSuccessToast({
                title: 'パスワードリセット',
                description: 'パスワード再設定メールを送信しました。',
            });
        },
        onError(error) {
            console.error('Failed to reset password:', error);
            showErrorToast({
                title: 'パスワードリセット',
                description: 'パスワード再設定メールの送信に失敗しました。',
            });
        },
    });

    const resetUserMfaMutation = useMutation({
        mutationFn: ({ organizationId, uuid }: { organizationId: string; uuid: string }) => {
            return resetGeneralUserMfa(organizationId, uuid);
        },
        onSuccess() {
            showSuccessToast({
                title: 'MFAリセット',
                description: 'MFAをリセットし、ユーザーに通知メールを送信しました。',
            });
        },
        onError(error) {
            console.error('Failed to reset MFA:', error);
            showErrorToast({
                title: 'MFAリセット',
                description: 'MFAのリセットに失敗しました。',
            });
        },
    });

    const handleAddUser = async (data: AddUserFormData) => {
        await createUserMutation.mutateAsync(data);
        setIsAddUserDialogOpen(false);
    };

    return (
        <Section>
            <SectionHeader
                title="ユーザー"
                extras={
                    <Flex justify="space-between" align="center" gap="2">
                        <Pill>{users.total}人</Pill>
                        <Button variant="primary" onClick={() => setIsAddUserDialogOpen(true)}>
                            <Icon icon={Plus} />
                            ユーザーを追加する
                        </Button>

                        <UserFormDialog
                            mode="add"
                            userRoles={userRoles}
                            isOpen={isAddUserDialogOpen}
                            onOpenChange={setIsAddUserDialogOpen}
                            isSubmitting={createUserMutation.isPending}
                            onSubmit={handleAddUser}
                        />
                    </Flex>
                }
                description="クライアントの企業アカウントに、クライアント側に Pasona Engagement を管理するユーザーを追加できます。追加後、ユーザーにはメールで通知されます。"
            />

            <UsersSection
                users={users}
                userRoles={userRoles}
                isUpdatePending={editUserMutation.isPending}
                isDeletePending={deleteUserMutation.isPending}
                isResetPasswordPending={resetUserPasswordMutation.isPending}
                isResetMfaPending={resetUserMfaMutation.isPending}
                onUpdate={editUserMutation.mutateAsync}
                onDelete={deleteUserMutation.mutateAsync}
                onResetPassword={resetUserPasswordMutation.mutateAsync}
                onResetMfa={resetUserMfaMutation.mutateAsync}
            />
        </Section>
    );
}

function SystemShowOrganizationUsersRouteSkeleton() {
    return (
        <Section>
            <Flex direction="column" gap="1" as="header">
                <Flex justify="space-between" align="center" gap="3">
                    <Skeleton.Line width="20%" height="xl" />
                    <Flex gap="2" align="center">
                        <Skeleton.Box width={50} height={28} />
                        <Skeleton.Box width={140} height={36} />
                    </Flex>
                </Flex>
                <Skeleton.Line width="60%" height="sm" />
            </Flex>

            <UsersSectionSkeleton />
        </Section>
    );
}
